import * as RadixSelect from '@radix-ui/react-select'
import { Check, ChevronDown } from 'lucide-react'
import { SearchSelect } from '@/components/ui/SearchSelect'
import { cn } from '@/lib/utils'

export interface SelectOption {
  value: string
  label: string
}

interface SelectProps {
  value: string
  onValueChange: (value: string) => void
  options: SelectOption[]
  placeholder?: string
  className?: string
  disabled?: boolean
  id?: string
  ariaLabelledby?: string
  /** Forceert wel/geen zoekveld; standaard bepaalt de lengte van de lijst dat. */
  searchable?: boolean
  /** Alleen met een zoekveld: de tekst in het zoekveld. */
  searchPlaceholder?: string
}

/**
 * Vanaf deze lengte kost scannen meer tijd dan typen, dus krijgt de lijst een
 * zoekveld. Kort en vast (rollen, frequenties, huisstijl) blijft een gewone
 * select: daar is een zoekveld alleen ruis.
 */
const SEARCHABLE_FROM = 8

export function Select({
  value,
  onValueChange,
  options,
  placeholder,
  className,
  disabled,
  id,
  ariaLabelledby,
  searchable,
  searchPlaceholder,
}: SelectProps) {
  if (searchable ?? options.length >= SEARCHABLE_FROM) {
    return (
      <SearchSelect
        value={value}
        onValueChange={onValueChange}
        options={options}
        placeholder={placeholder}
        searchPlaceholder={searchPlaceholder}
        className={className}
        disabled={disabled}
        id={id}
        ariaLabelledby={ariaLabelledby}
      />
    )
  }

  return (
    <RadixSelect.Root value={value} onValueChange={onValueChange} disabled={disabled}>
      <RadixSelect.Trigger
        id={id}
        aria-labelledby={ariaLabelledby}
        className={cn(
          'inline-flex h-9 items-center justify-between gap-2 rounded-lg border border-hairline bg-surface px-3 text-sm text-ink',
          'focus:border-accent-500/50 focus:outline-hidden focus:ring-2 focus:ring-accent-500/25 data-placeholder:text-ink-faint',
          'disabled:cursor-not-allowed disabled:opacity-60',
          className,
        )}
      >
        <RadixSelect.Value placeholder={placeholder} />
        <RadixSelect.Icon>
          <ChevronDown className="h-4 w-4 text-ink-faint" />
        </RadixSelect.Icon>
      </RadixSelect.Trigger>
      <RadixSelect.Portal>
        <RadixSelect.Content
          position="popper"
          sideOffset={6}
          className="z-50 min-w-(--radix-select-trigger-width) overflow-hidden rounded-card bg-surface p-1 shadow-pop animate-fade-in"
        >
          <RadixSelect.Viewport>
            {options.map((option) => (
              <RadixSelect.Item
                key={option.value}
                value={option.value}
                className="relative flex cursor-default select-none items-center rounded-md px-2.5 py-1.5 pr-7 text-sm text-ink outline-hidden data-highlighted:bg-fill-2 data-[state=checked]:font-medium"
              >
                <RadixSelect.ItemText>{option.label}</RadixSelect.ItemText>
                <RadixSelect.ItemIndicator className="absolute right-2 inline-flex items-center">
                  <Check className="h-4 w-4 text-accent-600" />
                </RadixSelect.ItemIndicator>
              </RadixSelect.Item>
            ))}
          </RadixSelect.Viewport>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  )
}
